import { makeAutoObservable } from 'mobx';
import {
	EntityAPI,
	EntityType,
	IEntityAPI,
	IEntityColumn,
	IEntityDashboardConfigurationTemplate,
	IEntityTypeUserInfo,
	IGenericEntity,
	ITaskRequestFilterField,
	IUserConfigPackage,
	IViewInfo,
} from '../API';
import { ViewsState } from '../components/controls/ViewsCtrl';
import { FiltersState } from './FiltersState';
import VersionHistoryState from './VersionHistoryState';

export default class EntityDashboardState {
	public loaded: boolean = false;
	public loading: boolean = false;

	public searchText: string = '';
	public pageLink: string = '';
	public sortField: string = '';
	public sortAsc: boolean = true;
	public isSearchable: boolean;

	public timeTravelDate: Date | null = null;

	private getApi: () => Promise<IEntityAPI<IGenericEntity>>;

	public items: IGenericEntity[] = [];
	public selectedItems: IGenericEntity[] = [];
	public filtersState: FiltersState;
	public allColumns: IEntityColumn[] = [];

	//The columns which are shown in the DetailsList
	public tableDisplayColumnInternalNames: string[] = [];

	public versionHistoryState: VersionHistoryState | null = null;

	public showColumnSelector: boolean = false;
	public viewsState: ViewsState;
	showItemVersions: boolean;
	userConfig: IUserConfigPackage;
	private preloadedIds: string[] | null = null;

	public newItemDefaults?: any;
	private _config: IEntityTypeUserInfo | null = null;
	private entityType: string;

	public showDeleteConfirmDialog: boolean = false;

	constructor(
		entityType: string | EntityType,
		userConfig: IUserConfigPackage,
		showItemVersions: boolean = true,
		allowSearching: boolean = true,
	) {
		makeAutoObservable(this);

		this.entityType = userConfig.entityTypes.getTypeId(entityType);

		this.getApi = async () => {
			return await EntityAPI.fromEntityTypeOrId(entityType, userConfig);
		};

		this.userConfig = userConfig;
		this.isSearchable = allowSearching;
		this.showItemVersions = showItemVersions;

		this.viewsState = new ViewsState(
			'Report',
			this.getApi,
			this.setSelectedView.bind(this),
			() => '',
			this.getCurrentViewConfig.bind(this),
			false,
		);
		this.filtersState = new FiltersState(
			userConfig,
			this.getApi,
			(newFilters) => {
				this.setFilters(newFilters);
				this.load();
			},
		);
	}

	public showNewForm() {
		
	}

	public showEditForm(id: string) {
		
	}

	public async confirmSelectedItemDeletion(): Promise<void> {
		let selectedIds = this.selectedItems.map((i) => i.Id);
		let api = await this.getApi();
		await api.delete(selectedIds);
		this.removeDeletedItems(selectedIds);
		this.toggleDeleteConfirmDialog(false);
	}

	public toggleDeleteConfirmDialog(arg0: boolean) {
		this.showDeleteConfirmDialog = arg0;
	}

	public setNewItemDefaults(item: any) {
		this.newItemDefaults = item;
	}

	public getConfig(): IEntityTypeUserInfo {
		if (!this._config) {
			this._config = this.userConfig.entityTypes.getConfig(
				this.entityType,
			) as IEntityTypeUserInfo;
		}

		return this._config || ({} as IEntityTypeUserInfo);
	}

	//For search results, we only want to show pre-selected IDs
	public setSearchResultIds(ids: string[]) {
		this.preloadedIds = ids;
	}

	public toggleShowColumnSelector() {
		this.showColumnSelector = !this.showColumnSelector;
	}

	public setTableDisplayColumns(columns: string[]) {
		this.tableDisplayColumnInternalNames = columns;
	}

	public setColumnViewable(columnName: string, viewable: boolean) {
		if (viewable) {
			this.tableDisplayColumnInternalNames =
				this.tableDisplayColumnInternalNames.concat([columnName]);
		} else {
			this.tableDisplayColumnInternalNames =
				this.tableDisplayColumnInternalNames.filter((c) => c !== columnName);
		}
	}

	public removeDeletedItems(ids: string[]) {
		this.items = this.items.filter((f) => ids.indexOf(f.Id) < 0);
	}

	public setSelection(items: IGenericEntity[]) {
		this.selectedItems = items;
	}

	//Called when the control loads, and data can be refreshed at that point if needs be.
	public async ensureLoaded() {
		if (!this.loaded && !this.loading) {
			//Perform initial load of columns
			this.allColumns = await (await this.getApi()).columns();
			this.setSelectedView(null);

			this.filtersState.setFilterFields(
				this.allColumns.filter((c) => (c.id || '').toLowerCase() !== 'title'),
			);

			this.versionHistoryState = this.showItemVersions
				? new VersionHistoryState(this.userConfig, this.getApi, this.allColumns)
				: null;

			await this.load();
		}
	}

	//Sets filters that don't get cleared by the view selection
	public setPermaFilters(filters: ITaskRequestFilterField[]) {
		this.items = [];
		this.filtersState.setPermaFilters(filters);
		this.pageLink = '';
		this.loaded = false;
	}

	//Sets standard filters which are also controlled by views and the filter panel
	public setFilters(filters: ITaskRequestFilterField[]) {
		this.items = [];
		this.filtersState.setFilters(filters);
		this.pageLink = '';
		this.loaded = false;
	}

	public setTimeTravelDate(date: Date | null) {
		this.timeTravelDate = date;
		this.reload();
	}

	public setSorting(fieldName: string, sortAsc: boolean) {
		this.sortField = fieldName;
		this.sortAsc = sortAsc;
		this.reload();
	}

	public setSearchText(searchText: string) {
		this.searchText = searchText;
		this.reload();
	}

	public async reload() {
		if (!this.loaded) {
			//If we haven't loaded in the first place, no need to reload.
			return;
		}

		this.items = [];
		this.selectedItems = [];
		this.pageLink = '';
		await this.load();
	}

	public async load(): Promise<void> {
		this.loading = true;
		let api = await this.getApi();
		let result = await api.query({
			parentId: '',
			searchText: this.searchText,
			ids: this.preloadedIds || [],
			queryTypes: [],
			pageLink: this.pageLink,
			filterFields: this.filtersState.getAllFilters(),
			sortField: this.sortField,
			sortAsc: this.sortAsc,
			pageSize: 100,
			modifiedSince: '',
			viewId: this.viewsState.selected?.id || '',
			timeTravel: this.timeTravelDate ? this.timeTravelDate.toISOString() : '',
		});

		if (result) {
			this.items = this.pageLink ? this.items.concat(result.items) : result.items;
			this.pageLink = result.nextPageLink;
		}

		this.loading = false;
		this.loaded = true;
	}

	public getCurrentViewConfig(): IEntityDashboardConfigurationTemplate {
		return {
			tableDisplayColumnInternalNames: this.tableDisplayColumnInternalNames,
			filterFields: this.filtersState.selectedFilters,
			sortField: this.sortField,
			sortAsc: this.sortAsc,
		};
	}

	public async setSelectedView(t: IViewInfo | null) {
		if (t) {
			this.setTableDisplayColumns(t.config.tableDisplayColumnInternalNames);
			this.sortField = t.config.sortField;
			this.sortAsc = t.config.sortAsc;
			this.filtersState.setFilters(t.config.filterFields);
			this.filtersState.applyFilters();
		} else {
			//Reset everything
			this.setTableDisplayColumns(
				this.allColumns.filter((c) => c.showInDefaultView).map((c) => c.id),
			);
			this.sortField = '';
			this.sortAsc = true;
			this.filtersState.setFilters([]);
			this.filtersState.applyFilters();
		}
	}
}
